<template>
  <Row
    spacing="md"
    class="invoice-settings"
  >
    <Column
      :xs="24"
      :md="12"
    >
      <FormGroup
        prop="language"
        :label="$t('form.invoiceForm.fields.language.label')"
        floating-label
        :input-value="language"
      >
        <Select
          class="select-v2 select-v2--with-prefix"
          name="language"
          v-model="language"
          autocomplete="new-password"
          data-test="choose_language"
          filterable
          :popper-append-to-body="false"
        >
          <Option
            v-for="option in languageOptions"
            :key="option.key"
            :label="option.value"
            :value="option.key"
          >
            <div :class="`vti__flag ${flagCodes[option.key]}`" />
            <span> {{ option.value }} </span>
          </Option>
          <template slot="prefix">
            <div :class="`vti__flag ${flagCodes[language]}`" />
          </template>
        </Select>
      </FormGroup>

      <CurrencySelect />

      <FormGroup
        prop="vat_totals_currency_conversion"
        v-if="visibilities.vat_totals_currency_conversion"
      >
        <Checkbox
          v-model="vat_totals_currency_conversion"
          data-test="vat_totals_currency_conversion"
        >
          {{ $t('form.invoiceForm.fields.currency.inputHelp') }}
        </Checkbox>
      </FormGroup>

      <FormGroup
        class="foot-note-form-group"
        prop="foot_note"
        :label="$t('form.invoiceForm.fields.foot_note.label')"
        :help="$t('form.invoiceForm.fields.foot_note.inputHelp')"
        spacing="xs"
        v-if="visibilities.foot_note"
        floating-label
        :input-value="foot_note"
      >
        <TextInput
          class="foot-note-input"
          name="foot_note"
          type="textarea"
          rows="4"
          v-model="foot_note"
          data-test="note"
          id="invoice_foot_note"
        />
        <span
          class="optional"
        >
          {{ $t('form.invoiceForm.headers.optional') }}
        </span>
      </FormGroup>

      <FormGroup
        class="qr-code-form-group"
        :label="$t('form.invoiceForm.fields.show_qr_code.label')"
        prop="show_qr_code"
        :help="$t('form.invoiceForm.fields.show_qr_code.hint')"
        v-if="visibilities.show_qr_code"
        spacing="xs"
        floating-label
        has-input-value
      >
        <Checkbox
          v-model="show_qr_code"
          data-test="show_qr_code"
          id="invoice_show_qr_code"
        >
          <strong>{{ $t('form.invoiceForm.fields.show_qr_code.desc') }}</strong>
        </Checkbox>
      </FormGroup>

      <FormGroup
        prop="rounding_type"
        :label="$t('form.invoiceForm.fields.rounding_type.label')"
        floating-label
        :input-value="rounding_type"
      >
        <Select
          class="select-v2"
          name="rounding_type"
          data-test="rounding_type"
          v-model="rounding_type"
          filterable
          :popper-append-to-body="false"
        >
          <Option
            v-for="option in roundingTypeOptions"
            :key="option.key"
            :label="option.value"
            :value="option.key"
            :data-test="`rounding_${option.key}`"
          />
        </Select>
      </FormGroup>
    </Column>
    <Column
      :xs="24"
      :md="12"
    >
      <FormGroup
        v-if="isEetSite && isEetVisible && userHasPremiumPlan && isEetEnabled"
        prop="language"
        input-narrow
        :label="$t('form.invoiceForm.fields.eetSettings.label')"
      >
        <Button
          v-if="userHasPremiumPlan"
          data-test="open_eet_dialog"
          @click="openEetDialog()"
          :type="isEetEnabled ? 'success' : 'danger'"
        >
          {{ isEetEnabled?
            $t('form.invoiceForm.fields.eetSettings.eetEnabled')
            : $t('form.invoiceForm.fields.eetSettings.eetDisabled') }}
        </Button>
        <Anchor
          v-if="userHasPremiumPlan"
          data-test="open_eet_dialog"
          @click="openEetDialog()"
        >
          {{ $t('form.invoiceForm.fields.eetSettings.changeSettings') }}
        </Anchor>
      </FormGroup>

      <UploadFile
        class="invoice-settings__upload-file"
        name="logo"
        :disabled="fieldsDisabled.logo"
        :action="uploadUrl"
        :data="logoUploadData"
        :thumbnail-mode="true"
        list-type="picture"
        store-value-setter="invoiceForm/updateStoreValue"
        :label="$t('form.invoiceForm.fields.logo.label')"
        :help="fieldsDisabled.logo ?
          $t('form.invoiceForm.fields.logo.inputHelpDisabled') :
          $t('form.invoiceForm.fields.logo.inputHelp')"
        :on-upload-error="onUploadError"
        accept=".png,.jpg,.gif"
      />

      <UploadFile
        class="invoice-settings__upload-file"
        name="stamp"
        :action="uploadUrl"
        :data="stampUploadData"
        list-type="picture"
        :thumbnail-mode="true"
        store-value-setter="invoiceForm/updateStoreValue"
        :label="$t('form.invoiceForm.fields.stamp.label')"
        :help="$t('form.invoiceForm.fields.stamp.inputHelp')"
        :on-upload-error="onUploadError"
        accept=".png,.jpg,.gif"
      />
      <InvoiceAppearance />
      <FormGroup
        v-if="!fieldsDisabled.color"
        prop="invoice_color"
        class="color-pickers"
        :label="$t('form.invoiceForm.fields.color.label')"
        floating-label
        floating-label-no-colunm
        has-input-value
      >
        <RadioGroup
          class="el-radio-colors-group"
          v-model="color"
        >
          <ColorSelect
            label="#2980b9"
            data-test="invoice_color-blue"
          />
          <ColorSelect
            label="#29b998"
            data-test="invoice_color-green"
          />
          <ColorSelect
            label="#e06001"
            data-test="invoice_color-orange"
          />
          <ColorSelect
            label="#5529b9"
            data-test="invoice_color-purple"
          />
          <label class="el-radio-help">
            <span class="el-radio__label">
              {{ $t('form.invoiceForm.fields.color.help') }}
            </span>
          </label>
          <ColorPicker
            v-model="color"
            :class="customPickerClass"
            :predefine="predefineColors"
          />
        </RadioGroup>
      </FormGroup>
    </Column>
  </Row>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import { mapState, mapMutations, mapGetters } from 'vuex';
import { includes } from 'ramda';
import isForcedEetAtPayment from '~/lib/validators/isForcedEetAtPayment';
import CurrencySelect from '../inputs/CurrencySelect.vue';

const FLAG_CODE = {
  cs: 'cz',
  en: 'gb',
  sk: 'sk',
  pl: 'pl',
  fr: 'fr',
  'de-DE': 'de',
  'en-RS': 'rs',
  'es-ES': 'es',
  'nl-NL': 'nl',
};

export default {
  components: {
    CurrencySelect,
    UploadFile: () => import('~/components/InvoiceForm/inputs/UploadFile.vue'),
    InvoiceAppearance: () => import('./InvoiceAppearance.vue'),
    ColorSelect: () => import('~/components/InvoiceForm/inputs/ColorSelect.vue'),
  },
  data: () => ({
    predefineColors: [
      '#002041',
      '#1b85b8',
      '#1e90ff',
      '#243447',
      '#303162',
      '#32bbb9',
      '#3e8672',
      '#559e83',
      '#5a5255',
      '#5f9ea0',
      '#6e37fa',
      '#7caaf0',
      '#ae5a41',
      '#af3f23',
      '#c3cb71',
      '#c71585',
      '#f40953',
      '#ff4500',
      '#ff9408',
      '#ffa93a',
    ],
    flagCodes: FLAG_CODE,
  }),
  computed: {
    ...mapFields('invoiceForm', [
      'currency',
      'rounding_type',
      'language',
      'vat_totals_currency_conversion',
      'currency_vat_conversion',
      'kind',
      'color',
      'foot_note',
      'show_qr_code',
    ]),
    ...mapState('invoiceForm', ['visibilities', 'means_of_payment']),
    ...mapGetters('invoiceForm', ['fieldsDisabled']),
    ...mapState('config', {
      roundingTypeOptions: state => state.invoice.rounding_types,
      languageOptions: state => state.invoice.languages,
      isEetEnabled: state => state.subscription['eet_enabled?'],
      isEetVisible: state => state.subscription['eet_visible?'],
      isEetSite: state => state.global['eet_enabled?'],
    }),
    ...mapGetters('env', ['isCzech']),
    ...mapGetters('auth', ['currentPlanName', 'userHasPremiumPlan']),
    uploadUrl() {
      return `${process.env.FO_API_PATH}/uploads`;
    },
    stampUploadData() {
      return { type: 'stamp' };
    },
    logoUploadData() {
      return { type: 'logo' };
    },
    isCustomColor() {
      const colors = ['#2980b9', '#29b998', '#e06001', '#5529b9'];
      return !includes(this.color, colors);
    },
    customPickerClass() {
      return {
        'is-custom': this.isCustomColor,
        'is-predefined': !this.isCustomColor,
      };
    },
  },
  methods: {
    ...mapMutations('eetForm', ['openEetDialog']),
    onUploadError() {
      // TODO: FO-911
    },
  },
  watch: {
    means_of_payment(newValue) {
      this.$store.dispatch('invoiceForm/updateStoreValue', {
        name: 'register_eet',
        value: isForcedEetAtPayment(newValue),
      });
    },
  },
};
</script>

<style lang="scss" scoped="true">
.invoice-settings {
  padding-top: 1rem;
}

.design-style {
  margin-right: 0;
}

:deep() {
  .el-form-item {
    margin-bottom: rem(28px);
  }

  .is-predefined {
    .el-color-picker__color-inner {
      background-image:
        linear-gradient(
          90deg,
          rgb(177 56 56) 0%,
          rgb(255 234 0) 24%,
          rgb(29 253 253) 50%,
          rgb(17 0 255) 72%,
          rgb(252 69 69) 100%
        );
    }
  }

  .is-custom {
    .el-color-picker__trigger {
      border-color: $--color-primary;
    }
  }
}

.el-radio-help {
  margin-bottom: 0;
  .el-radio__label {
    padding-left: 0;
  }
}

.color-pickers {
  .el-form-item__content {
    .el-radio-colors-group {
      width: 100%;
      gap: 8px;
    }
  }
}

.invoice-settings__upload-file {
  :deep() {
    @media only screen and (max-width: ($--breakpoint-sm - 1px)) {
      .el-button, .el-upload {
        width: 100%;
      }
    }
  }
}

.qr-code-form-group {
  :deep() {
    .el-input.is-focused .el-input__label {
      transform: translate3d(-10px, -10px, 0);
    }
    .el-form-item__content {
      margin-top: 1rem;
    }
  }
}

.foot-note-input {
  :deep() {
    .el-input__inner {
      height: 7.5rem;
    }
  }
}

.foot-note-form-group {
  .optional {
    position: absolute;
    right: 5px;
    color: $--color-text-blue;
    font-size: .9rem;
    background-color: #ffffff;
    top: -10px;
    height: none;
    padding: 0 2px;
  }
}
</style>
